// Enhanced News Meditation Personalization Engine
// Handles dynamic intensity and deep biography integration

import { QuestionnaireData } from '@/types/category';
import { BiographyVariables } from '@/utils/biographyParser';
import { createEnvironmentContext } from '@/utils/environmentParser';

export interface NewsMeditationPersonalization {
  explicitIntensity: number;
  humorLevel: string;
  newsContent: string;
  biographyContext?: string;
  userName?: string;
}

export function createDynamicLanguageRules(explicitLevel: number, humorLevel: string) {
  const intensityConfig = {
    1: { profanity: 'none', frequency: 0, words: [] },
    2: { profanity: 'mild', frequency: 1, words: ['damn', 'hell', 'crap'] },
    3: { profanity: 'mild', frequency: 2, words: ['damn', 'hell', 'crap', 'shit'] },
    4: { profanity: 'moderate', frequency: 3, words: ['damn', 'hell', 'shit', 'bullshit'] },
    5: { profanity: 'moderate', frequency: 4, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell'] },
    6: { profanity: 'strong', frequency: 5, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell', 'ass'] },
    7: { profanity: 'strong', frequency: 6, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell', 'ass', 'bitch'] },
    8: { profanity: 'maximum', frequency: 7, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell', 'ass', 'bitch', 'motherfucker'] },
    9: { profanity: 'maximum', frequency: 8, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell', 'ass', 'bitch', 'motherfucker', 'asshole'] },
    10: { profanity: 'uncensored', frequency: 10, words: ['fuck', 'shit', 'damn', 'bullshit', 'hell', 'ass', 'bitch', 'motherfucker', 'asshole', 'clusterfuck'] }
  };

  const config = intensityConfig[explicitLevel as keyof typeof intensityConfig] || intensityConfig[5];

  const humorConfig = {
    'Conservative': {
      comedyTypes: ['gentle wordplay', 'light observations'],
      comedyFrequency: 'minimal',
      setup: 'subtle humor'
    },
    'Moderate': {
      comedyTypes: ['situational comedy', 'absurd imagery', 'wordplay', 'gentle self-deprecation'],
      comedyFrequency: 'balanced',
      setup: 'setup-punchline structure'
    },
    'Maximum Comedy': {
      comedyTypes: ['setup-punchline-twist', 'absurd imagery', 'wordplay', 'situational comedy', 'news cycle absurdity', 'media addiction jokes'],
      comedyFrequency: 'frequent',
      setup: 'full comedy treatment with multiple laugh moments'
    }
  };

  return {
    language: config,
    comedy: humorConfig[humorLevel as keyof typeof humorConfig] || humorConfig['Moderate']
  };
}

export function createEnhancedNewsPrompt(
  newsContent: string, 
  userName?: string, 
  biographyVars?: BiographyVariables,
  explicitLevel: number = 5,
  humorLevel: string = 'Moderate',
  environment?: string
): string {
  const rules = createDynamicLanguageRules(explicitLevel, humorLevel);
  
  const biographyIntegration = biographyVars ? `
DEEP PERSONALIZATION REQUIREMENTS:
- Name: ${biographyVars.names?.user_first_name || userName}
- Living Situation: ${biographyVars.locations?.home?.join(', ') || 'Not specified'}
- Work/Career: ${biographyVars.work?.job_title?.join(', ') || 'Not specified'}
- Family Context: ${biographyVars.relationships?.family_dynamics?.join(', ') || 'Not specified'}
- Current Stressors: ${biographyVars.stressors ? Object.values(biographyVars.stressors).flat().join(', ') : 'Not specified'}
- Personal Goals: ${biographyVars.goals ? Object.values(biographyVars.goals).flat().join(', ') : 'Not specified'}
- Values & Beliefs: ${biographyVars.values?.join(', ') || 'Not specified'}
- Hobbies & Interests: ${biographyVars.hobbies?.join(', ') || 'Not specified'}

INTEGRATION INSTRUCTIONS:
- Connect this news to their specific life circumstances
- Reference their work, family, or living situation when relevant
- Use their personal goals and victories to build resilience against news stress
- Address how this news might specifically impact their daily life
- Mention their values when discussing perspective and empowerment
- Use their hobbies or interests for grounding techniques if applicable
` : '';

  const environmentIntegration = environment ? `
${createEnvironmentContext(environment)}
ENVIRONMENTAL INTEGRATION:
- Use their current environment for raw grounding techniques and safe space creation
- Reference their physical setting to enhance the therapeutic experience with bold language
- Connect environmental elements to news anxiety relief and boundary-setting techniques
- MANDATORY: Include 2-3 environmental references that support the profane meditation flow
` : '';

  return `
CREATE a RAW, BOLD, THERAPEUTICALLY PROFANE news meditation with these specifications:

NEWS TO PROCESS: "${newsContent}"

LANGUAGE INTENSITY LEVEL: ${explicitLevel}/10
- Profanity Level: ${rules.language.profanity}
- Therapeutic Swearing Frequency: ${rules.language.frequency} per minute
- Allowed Words: ${rules.language.words.join(', ')}

HUMOR & COMEDY LEVEL: ${humorLevel}
- Comedy Types: ${rules.comedy.comedyTypes.join(', ')}
- Comedy Frequency: ${rules.comedy.comedyFrequency}
- Structure: ${rules.comedy.setup}

${biographyIntegration}${environmentIntegration}

CRITICAL STYLE REQUIREMENTS - MATCH THIS ENERGY:
- START with aggressive, no-bullshit commands ("Turn off the fucking news and sit your ass down")
- USE profane breathing instructions ("Take a long, deep, disrespectful breath")
- CREATE absurd but accurate metaphors ("News cycle spins faster than a caffeinated squirrel in a tornado")
- BUILD empowering identity shifts ("You're the CEO of Not Giving a Shit about clickbait")
- ESCALATE from aggressive validation to zen authority
- NEVER apologize for being direct - be therapeutically explosive about systems, not people
- END with badass inner peace ("You're floating in a sea of cosmic 'fuck this' on a spaceship called Boundaries")

GENERATION FLOW:
1. AGGRESSIVE OPENING: Bold command to stop news consumption and get into meditation position
2. PROFANE VALIDATION: Raw acknowledgment of how fucking exhausting today's specific news is
3. BREATHING WITH ATTITUDE: Disrespectful breath instructions that purge news anxiety
4. ABSURD REALITY CHECK: Hilarious but accurate observations about media addiction and news cycle insanity
5. BODY REBELLION: Tell their nervous system to unfuck itself from news-induced tension
6. EMPOWERED IDENTITY: Transform them into the CEO of their own peace, master of boundaries
7. COSMIC PERSPECTIVE: Float in zen authority above the chaos while staying grounded
8. BADASS CLOSING: Send them back into the world as a mindfulness rebel with unshakeable calm

TONE: Raw, direct, therapeutically profane, absurdly funny, ultimately empowering. Like a zen master who curses creatively and doesn't take any bullshit from manufactured anxiety.

CRITICAL: Make this feel like the most honest, unfiltered, empowering conversation about news stress they've ever had - therapeutic profanity that serves healing, not harm.
`;
}

export function processNewsMeditationSession(formData: QuestionnaireData, userBiography?: string): string {
  const explicitLevel = parseInt(String(formData.explicitLanguageLevel)) || 5;
  const humorLevel = formData.humorLevel || 'Moderate';
  
  return createEnhancedNewsPrompt(
    formData.currentNews || '',
    formData.name,
    undefined, // TODO: Parse biography variables if provided
    explicitLevel,
    humorLevel,
    formData.environment
  );
}

export function extractNewsMeditationVariables(formData: QuestionnaireData) {
  return {
    newsContent: formData.currentNews || '',
    explicitLevel: parseInt(String(formData.explicitLanguageLevel)) || 5,
    humorLevel: formData.humorLevel || 'Moderate',
    processingStyle: formData.preferredStyle || '',
    guidanceType: formData.voiceType || '',
    primaryGoal: formData.primaryGoal || '',
    sessionLength: formData.sessionLength || '',
    specificFocus: formData.specificFocus || ''
  };
}

export function createNewsMeditationPrompt(
  newsContent: string,
  personalization: NewsMeditationPersonalization
): string {
  const rules = createDynamicLanguageRules(
    personalization.explicitIntensity, 
    personalization.humorLevel
  );

  return createEnhancedNewsPrompt(
    newsContent,
    personalization.userName,
    undefined,
    personalization.explicitIntensity,
    personalization.humorLevel,
    undefined // Environment would be passed here if available
  );
}